import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import api from '../api/client';
import '../index.css';

const emptySku = () => ({ sku: '', asin: '', name: '', boxesNeeded: '', setsNeeded: '', boxesAdded: 0, setsAdded: 0 });
// marketplace '' = the server detects it from the Shipment ID (server/utils/marketplace.js)
const emptyShipment = () => ({ shipmentID: '', pickupDate: '', marketplace: '', skus: [emptySku()] });
export const MARKETPLACES = ['Amazon', 'Walmart', 'TikTok'];

// Same suffix rule as server/routes/assembly.js cleanSku: _NP / _FBM / _FBA / -V, incl. stacked
const cleanSku = (sku) => String(sku || '').trim().replace(/(?:_(?:NP|FBM|FBA)|-V)+$/i, '');

const lookupSku = async (sku) => {
  try {
    const { data } = await api.get('/api/getNameBySku', { params: { sku } });
    return { name: data?.name || '', asin: data?.asin || '' };
  } catch (error) {
    console.error('Error fetching name by SKU:', error);
    return { name: '', asin: '' };
  }
};

// One popup can hold several shipments (a Walmart CSV export lists all shipments of the day).
// onSubmit receives an array of { shipmentID, pickupDate, marketplace, skus }.
const AmazonShipmentsPopup = ({ isOpen, onClose, onSubmit, editData }) => {
  const [shipments, setShipments] = useState([emptyShipment()]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (editData) {
      setShipments([{
        shipmentID: editData.shipmentID,
        pickupDate: editData.pickupDate,
        marketplace: editData.marketplace || '',
        skus: editData.skus?.length ? editData.skus : [emptySku()],
      }]);
    } else {
      resetForm();
    }
  }, [editData]);

  const resetForm = () => {
    setShipments([emptyShipment()]);
    setError('');
  };

  const updateShipment = (si, patch) =>
    setShipments((prev) => prev.map((s, i) => (i === si ? { ...s, ...patch } : s)));

  // fn(sku) returns the fields to merge into that SKU row
  const patchSku = (si, idx, fn) =>
    setShipments((prev) => prev.map((s, i) => (i !== si ? s : {
      ...s,
      skus: s.skus.map((k, j) => (j !== idx ? k : { ...k, ...fn(k) })),
    })));

  const handleRemoveShipment = (si) =>
    setShipments((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== si) : [emptyShipment()]));

  const handleAddRow = (si) => updateShipment(si, { skus: [...shipments[si].skus, emptySku()] });

  const handleRemoveRow = (si, idx) => {
    const skus = shipments[si].skus.filter((_, j) => j !== idx);
    updateShipment(si, { skus: skus.length ? skus : [emptySku()] });
  };

  const handleInputChange = async (si, idx, field, value) => {
    patchSku(si, idx, () => ({ [field]: value }));
    if (field === 'sku' && value) {
      const { name, asin } = await lookupSku(value);
      // Keep a manually entered ASIN, fill only when empty
      patchSku(si, idx, (k) => ({ name, asin: k.asin || asin }));
    }
  };

  const handleCSVUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setLoading(true);
    setError('');
    Papa.parse(file, {
      complete: (result) => {
        const data = result.data;
        // Walmart format: header row includes "No. of boxes" (new) or "No. of master cases" (old)
        const firstRow = data[0] || [];
        const isWalmartFormat = firstRow.includes('No. of boxes') || firstRow.includes('No. of master cases');
        if (isWalmartFormat) {
          handleWalmartCSV(data);
        } else {
          handleAmazonCSV(data);
        }
      },
      error: () => {
        setError('Error parsing CSV file.');
        setLoading(false);
      },
    });
    event.target.value = '';
  };

  const handleAmazonCSV = (data) => {
    // Shipment ID in row 2, column B; SKU table header in row 10
    const shipmentID = data[1]?.[1] || '';
    const skuData = data.slice(9);
    const headers = skuData[0] || [];
    const asinIndex = headers.indexOf('ASIN');
    const totalBoxesIndex = headers.indexOf('Total boxes');
    const totalUnitsIndex = headers.indexOf('Total units');

    const parsedSkus = skuData.slice(1).map((row) => ({
      ...emptySku(),
      asin: row[asinIndex] || '',
      boxesNeeded: row[totalBoxesIndex] || '',
      setsNeeded: row[totalUnitsIndex] || '',
    }));

    fetchSkuData(parsedSkus).then((skus) => {
      setShipments([{ shipmentID, pickupDate: '', marketplace: 'Amazon', skus }]);
      setLoading(false);
    });
  };

  const handleWalmartCSV = (data) => {
    const headers = data[0];
    const skuIndex = headers.indexOf('SKU');
    const boxesIndex = headers.includes('No. of boxes')
      ? headers.indexOf('No. of boxes')
      : headers.indexOf('No. of master cases');
    const unitsIndex = headers.indexOf('Total units');
    // New export: column A (no header) holds the Walmart shipment ID, blocks repeat the header row.
    // Old export had no ID column → everything goes into one shipment with an empty ID.
    const hasIdColumn = !headers[0];

    const dataRows = data.slice(1).filter((row) => row[skuIndex] && row[skuIndex] !== 'SKU');

    // Group rows by shipment ID, preserving file order
    const groups = new Map();
    for (const row of dataRows) {
      const id = hasIdColumn ? (row[0] || '').trim() : '';
      if (!groups.has(id)) groups.set(id, []);
      groups.get(id).push({
        ...emptySku(),
        sku: cleanSku(row[skuIndex]),
        boxesNeeded: row[boxesIndex] || '',
        setsNeeded: row[unitsIndex] || '',
      });
    }

    Promise.all(
      [...groups].map(async ([shipmentID, skus]) => ({
        shipmentID,
        pickupDate: '',
        marketplace: 'Walmart',
        skus: await fetchSkuDataBySku(skus),
      }))
    ).then((parsed) => {
      setShipments(parsed.length ? parsed : [emptyShipment()]);
      setLoading(false);
    });
  };

  const fetchSkuDataBySku = (skus) =>
    Promise.all(skus.map(async (row) => (row.sku ? { ...row, ...(await lookupSku(row.sku)) } : row)));

  const fetchSkuData = (skus) =>
    Promise.all(
      skus.map(async (row) => {
        if (!row.asin) return row;
        try {
          const { data } = await api.get('/api/getSkuByAsin', { params: { asin: row.asin } });
          if (!data?.sku) console.warn(`No SKU found for ASIN: ${row.asin}`);
          return { ...row, sku: data?.sku || '', name: data?.name || '' };
        } catch (err) {
          console.error('Error fetching SKU:', err);
          return row;
        }
      })
    );

  const handleSubmit = () => {
    const label = (si) => (shipments.length > 1 ? `Shipment ${si + 1}: ` : '');
    for (const [si, s] of shipments.entries()) {
      if (!s.shipmentID || !s.pickupDate) {
        setError(`${label(si)}ShipmentID and Pickup Date Plan are required.`);
        return;
      }
      if (s.skus.some((k) => !k.sku)) {
        setError(`${label(si)}SKU is required for each row.`);
        return;
      }
    }
    const ids = shipments.map((s) => s.shipmentID);
    if (new Set(ids).size !== ids.length) {
      setError('Shipment IDs must be unique.');
      return;
    }
    onSubmit(shipments);
    resetForm();
    onClose();
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  const many = shipments.length > 1;

  return (
    <div className="amz-popup-overlay">
      <div className="amz-popup-content">
        <h2 className="amz-popup-title">{editData ? 'Edit Shipment' : 'Add New Shipment'}</h2>
        <p className="amz-popup-subtitle">Manage shipment details</p>
        <button className="amz-close-btn" onClick={handleCancel}>×</button>
        <input
          type="file"
          accept=".csv"
          onChange={handleCSVUpload}
          className="amz-csv-upload"
          id="csv-upload"
        />
        <label htmlFor="csv-upload" className="amz-csv-label">Upload CSV</label>
        {loading && <div className="amz-loading">Loading...</div>}
        <form className="amz-form">
          {shipments.map((s, si) => (
            <div key={si}>
              <h3 className="amz-form-subtitle">
                {many ? `Shipment ${si + 1} of ${shipments.length}` : 'Details'}
                {many && (
                  <button
                    type="button"
                    className="amz-cancel-btn"
                    style={{ float: 'right', padding: '2px 10px', fontSize: 12 }}
                    onClick={() => handleRemoveShipment(si)}
                  >
                    Remove shipment
                  </button>
                )}
              </h3>
              <div className="amz-form-group">
                <label>ShipmentID *</label>
                <input
                  type="text"
                  value={s.shipmentID}
                  onChange={(e) => updateShipment(si, { shipmentID: e.target.value })}
                  required
                />
              </div>
              <div className="amz-form-group">
                <label>Marketplace</label>
                <select
                  value={s.marketplace}
                  onChange={(e) => updateShipment(si, { marketplace: e.target.value })}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #e0e0e0', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', color: '#111' }}
                >
                  <option value="">Auto (by Shipment ID)</option>
                  {MARKETPLACES.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="amz-form-group">
                <label>Pickup Date Plan *</label>
                <input
                  type="date"
                  value={s.pickupDate}
                  onChange={(e) => updateShipment(si, { pickupDate: e.target.value })}
                  required
                />
              </div>
              <h3 className="amz-form-subtitle">SKUs</h3>
              <table className="amz-sku-table">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>ASIN</th>
                    <th>Name</th>
                    <th>Boxes Needed</th>
                    <th>Sets Needed</th>
                    <th>Boxes Added</th>
                    <th>Sets Added</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {s.skus.map((sku, idx) => (
                    <tr key={idx}>
                      <td>
                        <input
                          type="text"
                          value={sku.sku}
                          onChange={(e) => handleInputChange(si, idx, 'sku', e.target.value)}
                          required
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={sku.asin}
                          onChange={(e) => handleInputChange(si, idx, 'asin', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={sku.name}
                          onChange={(e) => handleInputChange(si, idx, 'name', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={sku.boxesNeeded}
                          onChange={(e) => handleInputChange(si, idx, 'boxesNeeded', e.target.value)}
                          min="0"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={sku.setsNeeded}
                          onChange={(e) => handleInputChange(si, idx, 'setsNeeded', e.target.value)}
                          min="0"
                        />
                      </td>
                      <td>
                        <input type="number" value={sku.boxesAdded} readOnly />
                      </td>
                      <td>
                        <input type="number" value={sku.setsAdded} readOnly />
                      </td>
                      <td>
                        <button type="button" onClick={() => handleRemoveRow(si, idx)}>🗑️</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button type="button" className="amz-add-more-btn" onClick={() => handleAddRow(si)}>
                Add More +
              </button>
            </div>
          ))}
          {error && <p className="amz-error">{error}</p>}
          <div className="amz-form-actions">
            <button type="button" className="amz-submit-btn" onClick={handleSubmit}>
              {many ? `Submit ${shipments.length} shipments` : 'Submit'}
            </button>
            <button type="button" className="amz-cancel-btn" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AmazonShipmentsPopup;